import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ZoomInOutlined, ZoomOutOutlined, AimOutlined } from "@ant-design/icons";
import type { CemeteryMapLayer, GeoJsonFeature, GravePositionProperties } from "./gisApi";
import { gisIdentityColor, gisIdentityStatusLabel, type GisIdentityStatus } from "./gisVocabulary";
import "./gisAdmin.css";

/** Thứ tự chú giải = thứ tự dải DANH TÍNH ở màn hồ sơ; không sắp theo số lượng. */
const IDENTITY_LEGEND_ORDER: readonly GisIdentityStatus[] = ["KNOWN", "PARTIAL", "UNKNOWN"];

export interface GisMapCanvasProps {
  readonly layers: readonly CemeteryMapLayer[];
  readonly selectedGraveId: string | null;
  readonly onSelectGrave: (feature: GeoJsonFeature) => void;
  /**
   * Bề mặt CHỈ ĐỌC — tab "Bản đồ" nhúng trong hồ sơ nghĩa trang. Ẩn dụng cụ BIÊN TẬP (la bàn,
   * HUD toạ độ con trỏ); chrome XEM (nền, zoom, thước tỉ lệ, chú giải) giữ nguyên — đó là nhu
   * cầu của người xem. Mặc định `false` để route riêng đi nguyên đường cũ.
   */
  readonly readOnly?: boolean;
}

type BasemapMode = "hybrid" | "satellite" | "street";

/** Nền bản đồ mặc định khi MỞ trang. Một hằng số cho CẢ HAI chỗ dùng nó — state khởi tạo và
    lớp tile gắn ngay lúc khởi tạo. Để hai chỗ tự khai riêng thì lệch nhau, và lệch nghĩa là
    bản đồ NHÁY nền sai một nhịp trước khi effect chuyển nền kịp chạy. */
const DEFAULT_BASEMAP: BasemapMode = "street";

interface GravePathStyle {
  readonly color: string;
  readonly weight: number;
  readonly fillColor: string;
  readonly fillOpacity: number;
}

interface GraveColor {
  readonly fill: string;
  readonly stroke: string;
}

interface SectionPathOptions {
  readonly color: string;
  readonly weight: number;
  readonly fillColor: string;
  readonly fillOpacity: number;
}

const SELECTED_STYLE: GravePathStyle = {
  color: "var(--ncc-color-focus)",
  weight: 3,
  fillColor: "var(--ncc-color-status-danger)",
  fillOpacity: 0.9,
};

/* Khoá màu nằm ở `gisVocabulary.ts` (một nguồn cho canvas + hộp kiểm lọc + rail + chú giải),
   bám `cemetery.grave.identity_state` (KNOWN/PARTIAL/UNKNOWN) chứ không bám contract —
   xem ghi chú lịch sử ở đầu `gisVocabulary.ts`. */
function graveColor(status: string | undefined): GraveColor {
  return gisIdentityColor(status);
}

const SECTION_STYLE: Record<string, SectionPathOptions> = {
  AREA: { color: "var(--ncc-primitive-slate-600)", weight: 1.6, fillColor: "var(--ncc-primitive-slate-400)", fillOpacity: 0.1 },
  BLOCK: { color: "var(--ncc-primitive-slate-500)", weight: 1.1, fillColor: "var(--ncc-primitive-slate-400)", fillOpacity: 0.06 },
  ROW: { color: "var(--ncc-primitive-slate-400)", weight: 0.8, fillColor: "var(--ncc-primitive-slate-400)", fillOpacity: 0.04 },
};

const DEFAULT_SECTION_STYLE: SectionPathOptions = {
  color: "var(--ncc-primitive-slate-600)",
  weight: 1.6,
  fillColor: "var(--ncc-primitive-slate-400)",
  fillOpacity: 0.1,
};

const BOUNDARY_STYLE = {
  color: "var(--ncc-color-text-primary)",
  weight: 2.2,
  dashArray: "8 6",
  fill: false,
};

function sectionStyle(type: string | undefined): SectionPathOptions {
  return SECTION_STYLE[type ?? "AREA"] ?? DEFAULT_SECTION_STYLE;
}

// L.geoJSON kỳ vọng kiểu @types/geojson; schema sinh của app có kiểu riêng nên ép cục bộ.
function asLeafletGeoJson(layer: CemeteryMapLayer | undefined) {
  return (layer?.featureCollection ?? { type: "FeatureCollection", features: [] }) as never;
}

export function GisMapCanvas({ layers, selectedGraveId, onSelectGrave, readOnly = false }: GisMapCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const basemapLayersRef = useRef<Record<BasemapMode, L.TileLayer> | null>(null);
  const overlayRef = useRef<L.FeatureGroup | null>(null);
  const gravesRef = useRef<L.FeatureGroup | null>(null);
  const layerByIdRef = useRef<Map<string, L.Path>>(new Map());
  const baseStyleRef = useRef<WeakMap<L.Path, GravePathStyle>>(new WeakMap());
  const selectedLayerRef = useRef<L.Path | null>(null);

  /* `onSelectGrave` là CALLBACK, không phải DỮ LIỆU. Giữ nó trong ref để danh tính của nó
     không lọt vào deps của effect dựng lớp mộ. Nếu để nguyên trong deps: cha truyền hàm mũi tên
     inline (`GisMapPage.tsx:343`) ⇒ mỗi lần bấm một mộ, cha render lại ⇒ tham chiếu mới ⇒ effect
     chạy lại ⇒ xoá và dựng lại cả 433 ô RỒI `fitToData()` ⇒ bản đồ đang zoom 20 tự tụt về mức
     vừa khít dữ liệu. Sửa ở phía CHA chỉ vá được một lời gọi; vá ở đây thì lời gọi nào cũng đúng. */
  const onSelectGraveRef = useRef(onSelectGrave);
  useEffect(() => {
    onSelectGraveRef.current = onSelectGrave;
  }, [onSelectGrave]);

  /* Cùng lý do như `onSelectGraveRef`: effect khởi tạo map chạy đúng MỘT lần (`[]`), nên đọc
     `readOnly` qua ref. Đưa nó thẳng vào deps là dựng lại cả bản đồ mỗi lần đổi giá trị. */
  const readOnlyRef = useRef(readOnly);
  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  const [basemap, setBasemap] = useState<BasemapMode>(DEFAULT_BASEMAP);
  const [hoverCoords, setHoverCoords] = useState<{ lon: number; lat: number } | null>(null);

  const boundaryLayer = useMemo(
    () => layers.find((l) => l.layerCode === "CEMETERY_BOUNDARY"),
    [layers],
  );
  const sectionLayer = useMemo(
    () => layers.find((l) => l.layerCode === "SECTION"),
    [layers],
  );
  const graveLayer = useMemo(
    () => layers.find((l) => l.layerCode === "GRAVE_POSITION"),
    [layers],
  );
  const graveFeatures = useMemo(
    () => graveLayer?.featureCollection?.features ?? [],
    [graveLayer],
  );

  const fitToData = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    let target: L.LatLngBounds | null = null;
    const overlayBounds = overlayRef.current?.getBounds();
    const graveBounds = gravesRef.current?.getBounds();
    if (overlayBounds?.isValid()) target = overlayBounds;
    if (graveBounds?.isValid()) target = target ? target.extend(graveBounds) : graveBounds;
    // Trong jsdom (test) map.getSize() = 0 ⇒ bỏ qua fit để tránh divide-by-zero.
    if (target?.isValid() && (map.getSize().x ?? 0) > 0) {
      map.fitBounds(target, { padding: [40, 40], maxZoom: 19 });
    }
  }, []);

  // 1) Khởi tạo map + basemap + controls (chạy một lần).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const map = L.map(el, {
      zoomControl: false,
      attributionControl: true,
      minZoom: 3,
      maxZoom: 20,
    });
    mapRef.current = map;

    const googleTile = (lyrs: string): L.TileLayer =>
      L.tileLayer(`https://mt{s}.google.com/vt/lyrs=${lyrs}&x={x}&y={y}&z={z}`, {
        subdomains: "0123",
        maxZoom: 21,
        attribution: "&copy; Google",
      });
    const base = {
      satellite: googleTile("s"),
      hybrid: googleTile("y"),
      street: googleTile("m"),
    };
    basemapLayersRef.current = base;
    base[DEFAULT_BASEMAP].addTo(map);

    overlayRef.current = L.featureGroup().addTo(map);
    gravesRef.current = L.featureGroup().addTo(map);

    L.control.scale({ imperial: false, position: "bottomleft" }).addTo(map);

    map.on("mousemove", (e: L.LeafletMouseEvent) => {
      // Chế độ chỉ đọc không dựng HUD, nên `hoverCoords` không có ai tiêu thụ. Vẫn set là kéo
      // theo một lần re-render React cho MỖI lần nhích chuột, để vẽ một thứ không tồn tại.
      if (readOnlyRef.current) return;
      setHoverCoords({ lon: e.latlng.lng, lat: e.latlng.lat });
    });
    map.on("mouseout", () => setHoverCoords(null));

    const layerById = layerByIdRef.current;

    return () => {
      map.off();
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
      gravesRef.current = null;
      layerById.clear();
      selectedLayerRef.current = null;
    };
  }, []);

  // 2) Chuyển basemap.
  useEffect(() => {
    const map = mapRef.current;
    const layers = basemapLayersRef.current;
    if (!map || !layers) return;
    (["satellite", "hybrid", "street"] as const).forEach((mode) => {
      const layer = layers[mode];
      if (mode === basemap) {
        if (!map.hasLayer(layer)) layer.addTo(map);
      } else if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });
  }, [basemap]);

  // 3) Ranh giới + phân khu/lô/hàng (polygon thật).
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    overlay.clearLayers();

    L.geoJSON(asLeafletGeoJson(boundaryLayer), {
      interactive: false,
      style: () => BOUNDARY_STYLE,
    }).addTo(overlay);

    L.geoJSON(asLeafletGeoJson(sectionLayer), {
      interactive: false,
      filter: (feature) => {
        const props = (feature?.properties ?? {}) as Record<string, unknown>;
        return props.sectionType === "AREA";
      },
      style: (feature) => {
        const props = (feature?.properties ?? {}) as Record<string, unknown>;
        const type = typeof props.sectionType === "string" ? props.sectionType : undefined;
        return sectionStyle(type);
      },
      onEachFeature: (feature, layer) => {
        const props = (feature?.properties ?? {}) as Record<string, unknown>;
        const name = typeof props.sectionName === "string" ? props.sectionName : "";
        const code = typeof props.sectionCode === "string" ? props.sectionCode : "";
        const label = name || (code ? `Khu ${code}` : "");
        if (label) {
          layer.bindTooltip(label, { sticky: true, direction: "center", className: "gis-section-label" });
        }
      },
    }).addTo(overlay);
  }, [boundaryLayer, sectionLayer]);

  // 4) Ô mộ — vẽ footprint (hình chữ nhật thật) / location (Point), rebuild khi dữ liệu đổi rồi fit.
  useEffect(() => {
    const map = mapRef.current;
    const graves = gravesRef.current;
    if (!map || !graves) return;

    graves.clearLayers();
    layerByIdRef.current.clear();
    baseStyleRef.current = new WeakMap();
    selectedLayerRef.current = null;

    const byId = new Map<string, GeoJsonFeature>();
    graveFeatures.forEach((feature) => {
      if (feature.id) byId.set(feature.id, feature);
    });

    L.geoJSON(asLeafletGeoJson(graveLayer), {
      pointToLayer: (_feature, latlng) => {
        const props = (_feature.properties ?? {}) as GravePositionProperties;
        const c = graveColor(props.identityStatus);
        return L.circleMarker(latlng, {
          radius: 6,
          color: c.stroke,
          weight: 1.5,
          fillColor: c.fill,
          fillOpacity: 0.9,
        });
      },
      style: (feature) => {
        const props = (feature?.properties ?? {}) as GravePositionProperties;
        const c = graveColor(props.identityStatus);
        return { color: c.stroke, weight: 1, fillColor: c.fill, fillOpacity: 0.85 };
      },
      onEachFeature: (feature, layer) => {
        const props = (feature?.properties ?? {}) as GravePositionProperties;
        const path = layer as L.Path;
        const c = graveColor(props.identityStatus);
        baseStyleRef.current.set(path, {
          color: c.stroke,
          weight: 1.5,
          fillColor: c.fill,
          fillOpacity: 0.85,
        });
        layer.bindTooltip(props.positionCode ?? "", { direction: "top", offset: [0, -6] });
        const appFeature = typeof feature.id === "string" ? byId.get(feature.id) : undefined;
        if (appFeature) layer.on("click", () => onSelectGraveRef.current(appFeature));
        if (typeof feature.id === "string") layerByIdRef.current.set(feature.id, path);
      },
    }).addTo(graves);

    fitToData();
    // KHÔNG có `onSelectGrave` ở đây — xem ghi chú ở `onSelectGraveRef`. Chỉ DỮ LIỆU đổi mới
    // được dựng lại lớp và fit lại bản đồ; chọn một mộ thì không.
  }, [graveFeatures, graveLayer, fitToData]);

  // 5) Highlight ô mộ đang chọn (nền đỏ + viền xanh ngọc, giống bản mẫu).
  useEffect(() => {
    const prev = selectedLayerRef.current;
    if (prev) {
      const base = baseStyleRef.current.get(prev);
      if (base) prev.setStyle(base);
      selectedLayerRef.current = null;
    }
    if (!selectedGraveId) return;
    const selected = layerByIdRef.current.get(selectedGraveId);
    if (!selected) return;
    selected.setStyle(SELECTED_STYLE);
    selected.bringToFront();
    selectedLayerRef.current = selected;
  }, [selectedGraveId, graveFeatures]);

  return (
    <div data-testid="gis-map-canvas" className="gis-canvas-container">
      {/* Vùng bản đồ Leaflet + các overlay điều khiển */}
      <div className="gis-map-viewport">
        <div ref={containerRef} className="gis-leaflet-container" aria-label="Bản đồ nghĩa trang" />

        {/* La bàn hướng Bắc — dụng cụ BIÊN TẬP: Leaflet core không xoay được nên nó luôn là
            hằng số "0° N", không mang thông tin gì thêm cho người xem. */}
        {readOnly ? null : (
          <div className="gis-compass-badge">
            <span className="gis-north-arrow">↑ BẮC</span>
            <span>0° N</span>
          </div>
        )}

        {/* Chuyển nền bản đồ */}
        <div className="gis-basemap-toggle" role="group" aria-label="Chọn nền bản đồ">
          <button
            type="button"
            className={`gis-basemap-button ${basemap === "satellite" ? "is-active" : ""}`}
            onClick={() => setBasemap("satellite")}
            aria-pressed={basemap === "satellite"}
          >
            Vệ tinh
          </button>
          <button
            type="button"
            className={`gis-basemap-button ${basemap === "hybrid" ? "is-active" : ""}`}
            onClick={() => setBasemap("hybrid")}
            aria-pressed={basemap === "hybrid"}
          >
            Hỗn hợp
          </button>
          <button
            type="button"
            className={`gis-basemap-button ${basemap === "street" ? "is-active" : ""}`}
            onClick={() => setBasemap("street")}
            aria-pressed={basemap === "street"}
          >
            Đường
          </button>
        </div>

        {/* Zoom / Fit controls */}
        <div className="gis-zoom-toolbar">
          <button
            type="button"
            onClick={() => mapRef.current?.zoomIn()}
            className="gis-zoom-button"
            title="Phóng to"
            aria-label="Phóng to"
          >
            <ZoomInOutlined />
          </button>
          <button
            type="button"
            onClick={() => mapRef.current?.zoomOut()}
            className="gis-zoom-button"
            title="Thu nhỏ"
            aria-label="Thu nhỏ"
          >
            <ZoomOutOutlined />
          </button>
          <button
            type="button"
            onClick={fitToData}
            className="gis-zoom-button"
            title="Về khung dữ liệu"
            aria-label="Về khung dữ liệu"
          >
            <AimOutlined />
          </button>
        </div>

        {/* HUD Toạ độ con trỏ thời gian thực — chỉ có nghĩa khi đang dò toạ độ để BIÊN TẬP. */}
        {readOnly ? null : (
          <div className="gis-live-coords-hud">
            Kinh độ: {hoverCoords ? hoverCoords.lon.toFixed(6) : "—"}° Đ · Vĩ độ:{" "}
            {hoverCoords ? hoverCoords.lat.toFixed(6) : "—"}° B (WGS 84)
          </div>
        )}
      </div>

      {/* Chú giải danh tính — cùng nguồn khoá/màu/nhãn với nét vẽ và hộp kiểm lọc. */}
      <div className="gis-legend-bar">
        <div className="gis-legend-items">
          <strong style={{ color: "var(--ncc-color-text-primary)" }}>Ký hiệu danh tính:</strong>
          {IDENTITY_LEGEND_ORDER.map((code) => {
            const color = gisIdentityColor(code);
            return (
              <div className="gis-legend-item" key={code}>
                <span
                  className="gis-legend-swatch"
                  style={{ background: color.fill, borderColor: color.stroke }}
                />
                <span>{gisIdentityStatusLabel(code)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="gis-legend-item">
            <span style={{ width: 14, height: 14, borderRadius: "2px", border: "2px solid var(--ncc-color-focus)", background: "var(--ncc-color-status-danger)", display: "inline-block" }} />
            <span>Vị trí đang chọn</span>
          </div>
          <span>Hệ quy chiếu: WGS 84 (EPSG:4326)</span>
        </div>
      </div>
    </div>
  );
}